/**
 * App Header — glass topbar (Talk2-style)
 *
 * Connection status indicator:
 * - Green + pulse = connected
 * - Yellow + spin = reconnecting
 * - Red          = disconnected
 */
import React from 'react';
import { Activity, Clock, LogOut, Map, Menu, Radio, Settings, Target, User } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import useStore from '../store/useStore';
import axios from 'axios';
import { getSocket } from '../hooks/useSocket';

export default function Header({ onMenuToggle }) {
  const user = useStore(s => s.user);
  const currentChannel = useStore(s => s.currentChannel);
  const socketStatus = useStore(s => s.socketStatus);
  const logout = useStore(s => s.logout);
  const navigate = useNavigate();
  const location = useLocation();

  const isAdmin = user?.role === 'admin';
  // Must match server-side viewLocations (roles table): tracking is for
  // Administrador + Supervisor de Operaciones only (2026-07-01).
  const MAP_ROLES = ['Administrador', 'Supervisor de Operaciones'];
  const canSeeMap = isAdmin || MAP_ROLES.includes(user?.customRole?.name);
  // Field roles get a decluttered header: radio-first, no replay/activity chrome
  const FIELD_ROLES = ['Agente de Campo', 'Inspector de Campo'];
  const isField = !isAdmin && FIELD_ROLES.includes(user?.customRole?.name);
  const isInAdmin = location.pathname.startsWith('/admin');

  const handleLogout = async () => {
    try {
      const socket = getSocket();
      if (socket?.connected) socket.disconnect();
      await axios.post('/api/auth/logout');
    } catch {}
    logout();
    navigate('/login');
  };

  // Connection status config (light theme)
  const statusConfig = {
    connected:    { dot: 'bg-s-green animate-pulse', label: 'Online',       labelColor: 'text-n-500'     },
    reconnecting: { dot: 'bg-s-orange animate-spin', label: 'Reconectando', labelColor: 'text-s-orange'  },
    disconnected: { dot: 'bg-s-red',                 label: 'Desconectado', labelColor: 'text-s-red'     },
    connecting:   { dot: 'bg-s-orange animate-pulse',label: 'Conectando',   labelColor: 'text-s-orange'  },
  };
  const sc = statusConfig[socketStatus] || statusConfig.disconnected;

  return (
    <header
      className="h-[60px] flex items-center px-4 gap-3 flex-shrink-0 relative z-10"
      style={{
        background: 'rgba(255,255,255,0.72)',
        WebkitBackdropFilter: 'saturate(180%) blur(30px)',
        backdropFilter: 'saturate(180%) blur(30px)',
        borderBottom: '1px solid rgba(0,0,0,0.06)',
      }}
    >
      {!isInAdmin && (
        <button
          onClick={onMenuToggle}
          type="button"
          aria-label="Abrir menú de canales"
          title="Menú"
          className="lg:hidden p-2 rounded-md text-n-600 hover:text-n-800 hover:bg-black/5 transition-colors"
        >
          <Menu size={20} />
        </button>
      )}

      {/* Brand / logo */}
      <button
        type="button"
        onClick={() => navigate('/')}
        className="flex items-center gap-2.5 cursor-pointer bg-transparent border-0 p-0"
      >
        <div className="w-8 h-8 rounded-md brand-conic flex items-center justify-center shadow-apple-sm">
          <span className="w-6 h-6 rounded bg-white flex items-center justify-center text-sm">🚀</span>
        </div>
        <span className="font-extrabold text-base tracking-apple-tighter hidden sm:block text-n-800">
          RocketTalk
        </span>
      </button>

      {/* Active channel pill — visible on all sizes for context */}
      {!isInAdmin && currentChannel && (
        <div
          className="flex items-center gap-1.5 ml-1 sm:ml-2 px-2 sm:px-3 py-1 sm:py-1.5 rounded-full max-w-[40vw] sm:max-w-none"
          style={{ background: '#E6F1FF', color: '#007AFF' }}
        >
          <Radio size={12} className="flex-shrink-0" />
          <span className="text-[11px] sm:text-xs font-semibold truncate">{currentChannel.name}</span>
        </div>
      )}

      {/* Admin breadcrumb — visible on all sizes for context */}
      {isInAdmin && (
        <div
          className="flex items-center gap-1.5 ml-1 sm:ml-2 px-2 sm:px-3 py-1 sm:py-1.5 rounded-full"
          style={{ background: '#FFEAEF', color: '#FF375F' }}
        >
          <Settings size={12} className="flex-shrink-0" />
          <span className="text-[11px] sm:text-xs font-semibold">Admin</span>
        </div>
      )}

      <div className="ml-auto flex items-center gap-2">
        {/* Connection status — always visible: field users must know when they are off-grid */}
        <div className="flex items-center gap-1.5" aria-live="polite">
          <div className={`w-2 h-2 rounded-full ${sc.dot}`} aria-hidden="true" />
          <span className={`text-xs font-medium hidden sm:inline ${sc.labelColor === 'text-n-500' ? 'text-n-600' : sc.labelColor}`}>{sc.label}</span>
        </div>

        {/* Profile */}
        <button
          onClick={() => navigate('/profile')}
          type="button"
          aria-label="Mi perfil"
          className="p-2 rounded-md text-n-600 hover:text-s-blue hover:bg-black/5 transition-all"
          title="Mi Perfil"
        >
          <User size={16} aria-hidden="true" />
        </button>

        {/* Map */}
        {!isInAdmin && canSeeMap && (
          <button
            onClick={() => navigate('/map')}
            type="button"
            aria-label="Mapa en tiempo real"
            className="p-2 rounded-md text-n-600 hover:text-s-blue hover:bg-black/5 transition-all"
            title="Mapa en Tiempo Real"
          >
            <Map size={16} aria-hidden="true" />
          </button>
        )}

        {/* Missions */}
        {!isInAdmin && (
          <button
            onClick={() => navigate('/missions')}
            type="button"
            aria-label="Misiones"
            className="p-2 rounded-md text-n-600 hover:text-s-purple hover:bg-black/5 transition-all"
            title="Misiones"
          >
            <Target size={16} aria-hidden="true" />
          </button>
        )}

        {/* Replay */}
        {!isInAdmin && !isField && (
          <button
            onClick={() => navigate('/replay')}
            type="button"
            aria-label="Replay de transmisiones"
            className="p-2 rounded-md text-n-600 hover:text-s-orange hover:bg-black/5 transition-all"
            title="Replay"
          >
            <Clock size={16} aria-hidden="true" />
          </button>
        )}

        {/* Activity */}
        {!isInAdmin && !isField && (
          <button
            onClick={() => navigate('/activity')}
            type="button"
            aria-label="Actividad reciente"
            className="p-2 rounded-md text-n-600 hover:text-s-mint hover:bg-black/5 transition-all"
            title="Actividad"
          >
            <Activity size={16} aria-hidden="true" />
          </button>
        )}

        {/* Admin entry */}
        {isAdmin && !isInAdmin && (
          <button
            onClick={() => navigate('/admin')}
            className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-all"
            style={{ background: '#FFEAEF', color: '#FF375F' }}
          >
            <Settings size={12} />
            Admin
          </button>
        )}

        {/* Back from admin */}
        {isInAdmin && (
          <button
            onClick={() => navigate('/')}
            className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-all"
            style={{ background: '#E6F1FF', color: '#007AFF' }}
          >
            ← RocketTalk
          </button>
        )}

        {/* User chip */}
        {user && (
          <div className="flex items-center gap-2">
            <div
              className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold text-white flex-shrink-0 shadow-apple-sm"
              style={{
                background: `linear-gradient(135deg, ${user.avatarColor || '#007AFF'}, ${user.avatarColor || '#5856D6'})`,
              }}
            >
              {(user.username?.[0] || '?').toUpperCase()}
            </div>
            <span className="text-sm font-semibold text-n-700 hidden sm:block">
              {user.username}
            </span>
          </div>
        )}

        {/* Logout */}
        <button
          onClick={handleLogout}
          type="button"
          aria-label="Cerrar sesión"
          className="p-2 rounded-md text-n-600 hover:text-s-red transition-all"
          title="Cerrar sesión"
        >
          <LogOut size={16} aria-hidden="true" />
        </button>
      </div>
    </header>
  );
}
